/**
 * CRM Clients list page
 */
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { listClients, createClient } from '../../services/crm'
import { formatRelativeTime, formatLocalDateTime } from '../../utils/formatDateTime'
import UserSelect from '../../components/UserSelect'
import Modal, { ModalFooter } from '../../components/Modal'
import FilterBar from '../../components/FilterBar'
import PageHeading from '../../components/PageHeading'
import { CLIENTS_HELP } from '../../content/pageHelp'
import { useUserOptions, userLabel } from '../../utils/userOptions'
import { facetFilterOptions, useLicenseFacets } from '../../utils/facetOptions'
import {
  EXPIRY_WINDOW_OPTIONS, PRIORITY_FILTER_OPTIONS, windowDays,
} from '../../utils/filterOptions'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { useTableSort } from '../../hooks/useTableSort'
import {
  isCode, isIdFilter, isPageNumber, isStateCode, listStateKey, textUpTo, useArrivedByLink,
  usePageSize, useSessionState,
} from '../../hooks/useSessionState'
import { Pagination } from '../../components/Pagination'
import { SortableHeader } from '../../components/SortableHeader'
import type { Client, ClientCreate, AccountStatus, ClientPriority } from '../../types'

/** Must match `_CLIENT_SORTS` in backend/app/api/v1/crm.py — the backend rejects anything else. */
type SortKey = 'entity_name' | 'account_status' | 'priority'
const SORT_KEYS: readonly SortKey[] = ['entity_name', 'account_status', 'priority']

// Filters, page and sort are remembered for the tab (useSessionState), so opening a
// client and coming Back finds the list as it was left.
const KEY = listStateKey('clients')

const STATUS_LABELS: Record<AccountStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  prospect: 'Prospect',
}

const STATUS_COLORS: Record<AccountStatus, string> = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-gray-100 text-gray-600',
  prospect: 'bg-blue-100 text-blue-800',
}

function StatusBadge({ status }: { status: AccountStatus }) {
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_COLORS[status]}`}>
      {STATUS_LABELS[status]}
    </span>
  )
}

/**
 * The other half of the client-status reshape. `account_status` says whether we want
 * them; this says whether there is anything to do. Computed by the backend on every
 * read (see app/services/client_status.py), so logging a call or booking a task clears
 * it here, on the detail page and on the dashboard at once.
 */
export const STALE_EXPLANATION =
  'Nothing live here: no license in force or recently lapsed, no contact logged, ' +
  'no task open or booked, and no mail queued.'

export function StaleBadge() {
  return (
    <span
      className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800"
      title={STALE_EXPLANATION}
    >
      Stale
    </span>
  )
}

const PRIORITY_LABELS: Record<ClientPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  urgent: 'Urgent',
}

const PRIORITY_COLORS: Record<ClientPriority, string> = {
  low: 'bg-gray-100 text-gray-500',
  medium: 'bg-blue-50 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700 font-semibold',
}

function PriorityBadge({ priority }: { priority: ClientPriority }) {
  return (
    <span className={`px-2 py-0.5 text-xs rounded-full ${PRIORITY_COLORS[priority]}`}>
      {PRIORITY_LABELS[priority]}
    </span>
  )
}

const EMPTY_FORM: ClientCreate = {
  entity_name: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  account_status: 'active',
  priority: 'medium',
  notes: '',
}

export default function ClientsPage() {
  const navigate = useNavigate()

  const [clients, setClients] = useState<Client[]>([])
  const [total, setTotal] = useState(0)
  // A dashboard link (?stale=1, ?ownership_change=1) shows exactly what it promised:
  // its own filter on a clean slate, not on top of whatever was left set last time.
  const [deepLink] = useState(() => {
    const q = new URLSearchParams(window.location.search)
    return { ownershipChange: q.get('ownership_change') === '1', stale: q.get('stale') === '1' }
  })
  const arrivedByLink = useArrivedByLink()
  const fresh = { fresh: arrivedByLink && (deepLink.ownershipChange || deepLink.stale) }

  const [page, setPage] = useSessionState(KEY('page'), 1, isPageNumber, fresh)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [filterStatus, setFilterStatus] = useSessionState(KEY('status'), '', isCode, fresh)
  const [filterSearch, setFilterSearch] = useSessionState(KEY('search'), '', textUpTo(200), fresh)
  // Debounced so a typed query is one request, not one per keystroke.
  const debouncedSearch = useDebouncedValue(filterSearch)
  // '' = everyone · 'mine' · 'none' (unassigned) · a user id as a string
  const [filterAssignee, setFilterAssignee] = useSessionState(KEY('assignee'), '', isIdFilter, fresh)
  const [filterPriority, setFilterPriority] = useSessionState(KEY('priority'), '', isCode, fresh)
  const [filterState, setFilterState] = useSessionState(KEY('state'), '', isStateCode, fresh)
  const [filterExpiry, setFilterExpiry] = useSessionState(KEY('expiry'), '', isCode, fresh)
  const [filterOpenTasks, setFilterOpenTasks] = useSessionState(KEY('openTasks'), false, undefined, fresh)
  const [filterOwnershipChange, setFilterOwnershipChange] = useSessionState(
    KEY('ownershipChange'), deepLink.ownershipChange, undefined, fresh,
  )
  const [filterStale, setFilterStale] = useSessionState(KEY('stale'), deepLink.stale, undefined, fresh)

  const users = useUserOptions()
  const facets = useLicenseFacets()

  /** Any filter change re-reads page 1 — page 3 of the old list is not page 3 of the new one. */
  function applyFilter<T>(set: (v: T) => void) {
    return (value: T) => { set(value); setPage(1) }
  }
  // Sorting is server-side: the list is paged, so a comparator over the 25 rows in hand
  // could only ever reorder the page. No `descFirst` — all three keys are text or a
  // severity band, and ascending is what a first click on either means.
  const { sortKey, sortOrder, handleSort } = useTableSort<SortKey>(
    'entity_name', 'asc',
    { onSortChange: () => setPage(1), persist: { key: KEY('sort'), keys: SORT_KEYS } },
  )

  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState<ClientCreate>(EMPTY_FORM)
  const [createError, setCreateError] = useState('')
  const [creating, setCreating] = useState(false)

  const [pageSize, setPageSize] = usePageSize('clients', 25)

  useEffect(() => {
    load()
  }, [page, filterStatus, debouncedSearch, filterAssignee, filterPriority, filterState, // eslint-disable-line react-hooks/exhaustive-deps
      filterExpiry, filterOpenTasks, filterOwnershipChange, filterStale, sortKey, sortOrder, pageSize])

  async function load() {
    setLoading(true)
    setError('')
    try {
      const res = await listClients({
        page,
        page_size: pageSize,
        account_status: filterStatus || undefined,
        priority: filterPriority || undefined,
        state: filterState || undefined,
        expiring_within_days: windowDays(filterExpiry),
        has_open_tasks: filterOpenTasks || undefined,
        has_ownership_change: filterOwnershipChange || undefined,
        stale: filterStale || undefined,
        search: debouncedSearch || undefined,
        sort: sortKey,
        order: sortOrder,
        my_clients: filterAssignee === 'mine' || undefined,
        unassigned: filterAssignee === 'none' || undefined,
        assigned_to_user_id:
          filterAssignee && filterAssignee !== 'mine' && filterAssignee !== 'none'
            ? parseInt(filterAssignee, 10)
            : undefined,
      })
      setClients(res.items)
      setTotal(res.total)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load clients')
    } finally {
      setLoading(false)
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!form.entity_name.trim()) {
      setCreateError('Entity name is required')
      return
    }
    setCreating(true)
    setCreateError('')
    try {
      const payload: ClientCreate = {
        entity_name: form.entity_name.trim(),
        account_status: form.account_status,
      }
      if (form.priority) payload.priority = form.priority
      if (form.contact_name?.trim()) payload.contact_name = form.contact_name.trim()
      if (form.contact_email?.trim()) payload.contact_email = form.contact_email.trim()
      if (form.contact_phone?.trim()) payload.contact_phone = form.contact_phone.trim()
      if (form.notes?.trim()) payload.notes = form.notes.trim()
      if (form.assigned_to_user_id) payload.assigned_to_user_id = form.assigned_to_user_id

      const client = await createClient(payload)
      setShowCreate(false)
      setForm(EMPTY_FORM)
      navigate(`/crm/clients/${client.id}`)
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Failed to create client')
    } finally {
      setCreating(false)
    }
  }

  const totalPages = Math.ceil(total / pageSize)

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <PageHeading title="Clients" help={CLIENTS_HELP} subtitle={`${total} total`} />
        <button
          onClick={() => { setShowCreate(true); setCreateError('') }}
          className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700"
        >
          + New Client
        </button>
      </div>

      {/* Filters — one shared bar, same controls and order as Leads and Tasks.
          "State" is the licensee's address on a linked license: a client record has
          no address of its own, and the filing agent's is often a different state. */}
      <FilterBar
        searchValue={filterSearch}
        onSearchChange={applyFilter(setFilterSearch)}
        searchPlaceholder="Search name, contact, call sign…"
        searchLabel="Search clients"
        selects={[
          {
            id: 'client-status',
            label: 'Filter clients by account status',
            value: filterStatus,
            onChange: applyFilter(setFilterStatus),
            options: [
              // `prospect` is retired — intent is now active/inactive only. A legacy
              // row still renders its badge; nothing offers the value any more.
              { value: '', label: 'All statuses' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ],
          },
          {
            id: 'client-priority',
            label: 'Filter clients by priority',
            value: filterPriority,
            onChange: applyFilter(setFilterPriority),
            options: PRIORITY_FILTER_OPTIONS,
          },
          {
            id: 'client-state',
            label: 'Filter clients by state',
            value: filterState,
            onChange: applyFilter(setFilterState),
            options: facetFilterOptions('All states', facets?.states, filterState),
          },
          {
            id: 'client-expiry',
            label: 'Filter clients by license expiry window',
            value: filterExpiry,
            onChange: applyFilter(setFilterExpiry),
            options: EXPIRY_WINDOW_OPTIONS,
          },
          {
            id: 'client-assignee',
            label: 'Filter clients by assignee',
            value: filterAssignee,
            onChange: applyFilter(setFilterAssignee),
            options: [
              { value: '', label: 'Anyone' },
              { value: 'mine', label: 'My clients' },
              { value: 'none', label: 'Unassigned' },
              ...users.map((u) => ({ value: String(u.id), label: userLabel(u) })),
            ],
          },
        ]}
        extraActiveCount={
          (filterOpenTasks ? 1 : 0) + (filterOwnershipChange ? 1 : 0) + (filterStale ? 1 : 0)
        }
        onClearExtras={() => {
          setFilterOpenTasks(false)
          setFilterOwnershipChange(false)
          setFilterStale(false)
          setPage(1)
        }}
      >
        <label className="flex items-center gap-1.5 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={filterOpenTasks}
            onChange={(e) => { setFilterOpenTasks(e.target.checked); setPage(1) }}
            className="rounded border-gray-300"
          />
          Has open tasks
        </label>
        <label className="flex items-center gap-1.5 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={filterOwnershipChange}
            onChange={(e) => { setFilterOwnershipChange(e.target.checked); setPage(1) }}
            className="rounded border-gray-300"
          />
          Ownership changed
        </label>
        <label className="flex items-center gap-1.5 text-sm text-gray-700" title={STALE_EXPLANATION}>
          <input
            type="checkbox"
            checked={filterStale}
            onChange={(e) => { setFilterStale(e.target.checked); setPage(1) }}
            className="rounded border-gray-300"
          />
          Stale
        </label>
      </FilterBar>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">{error}</div>
      )}

      {/* Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : clients.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No clients found.{' '}
            <button
              onClick={() => { setShowCreate(true); setCreateError('') }}
              className="text-primary-600 hover:underline"
            >
              Create one
            </button>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <SortableHeader
                    sortKey="entity_name" currentSortKey={sortKey} currentSortOrder={sortOrder}
                    onSort={handleSort} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    Name
                  </SortableHeader>
                  {/* Licenses, Last Contact and Tasks stay plain <th>: all three are
                      computed after the page is selected (crm.py, the grouped queries at
                      the end of list_clients), so the backend cannot order by them and a
                      clickable header would be claiming an ordering it does not have. */}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Licenses</th>
                  <SortableHeader
                    sortKey="account_status" currentSortKey={sortKey} currentSortOrder={sortOrder}
                    onSort={handleSort} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase"
                  >
                    Status
                  </SortableHeader>
                  <SortableHeader
                    sortKey="priority" currentSortKey={sortKey} currentSortOrder={sortOrder}
                    onSort={handleSort} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell"
                  >
                    Priority
                  </SortableHeader>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Last Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Tasks</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">Assigned To</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {clients.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => navigate(`/crm/clients/${c.id}`)}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900 text-sm">{c.entity_name}</span>
                      {c.ownership_change_count > 0 && (
                        <span
                          className="ml-2 inline-block rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 align-middle"
                          title="An FCC license this client holds changed entity name or FRN and has not been reviewed"
                        >
                          {c.ownership_change_count === 1
                            ? 'Ownership changed'
                            : `${c.ownership_change_count} ownership changes`}
                        </span>
                      )}
                      {c.contact_name && (
                        <div className="text-xs text-gray-500">{c.contact_name}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 hidden sm:table-cell">
                      {c.licenses.length === 0 ? (
                        <span className="text-gray-400">—</span>
                      ) : c.licenses.length === 1 ? (
                        <span className="font-mono">{c.licenses[0].call_sign || '—'}</span>
                      ) : (
                        <span className="font-mono">{c.licenses[0].call_sign || '—'}{' '}
                          <span className="text-gray-400 font-sans text-xs">+{c.licenses.length - 1}</span>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center gap-1">
                        <StatusBadge status={c.account_status} />
                        {c.is_stale && <StaleBadge />}
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <PriorityBadge priority={c.priority} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 hidden md:table-cell">
                      {c.last_contact_at ? (
                        <span title={formatLocalDateTime(c.last_contact_at)}>
                          {formatRelativeTime(c.last_contact_at)}
                        </span>
                      ) : (
                        <span className="text-gray-300">None</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm hidden md:table-cell">
                      {c.open_tasks_count > 0 ? (
                        <span className="font-semibold text-orange-600">{c.open_tasks_count}</span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 hidden lg:table-cell">
                      {c.contact_email || c.contact_phone || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 hidden lg:table-cell">
                      {userLabel(c.assigned_to)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Pagination
        page={page} totalPages={totalPages} onPageChange={setPage}
        total={total} pageSize={pageSize}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1) }}
      />

      {/* Create Modal */}
      {showCreate && (
        <Modal
          titleId="new-client-title"
          title="New Client"
          onClose={() => { setShowCreate(false); setForm(EMPTY_FORM) }}
          /* This modal never had a backdrop-click handler. Adding one now would be a
             new way to lose a half-filled form, so keep the existing behaviour. */
          closeOnBackdrop={false}
          maxWidth="max-w-md"
        >
          <form onSubmit={handleCreate} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Entity Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.entity_name}
              onChange={(e) => setForm({ ...form, entity_name: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              required
              maxLength={255}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={form.account_status}
                onChange={(e) => setForm({ ...form, account_status: e.target.value as AccountStatus })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value as ClientPriority })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
            <input
              type="text"
              value={form.contact_name}
              onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              maxLength={100}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                type="tel"
                value={form.contact_phone}
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                maxLength={30}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
            <UserSelect
              users={users}
              value={form.assigned_to_user_id ?? null}
              onChange={(id) => setForm({ ...form, assigned_to_user_id: id ?? undefined })}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          {createError && (
            <p className="text-sm text-red-600">{createError}</p>
          )}

          <ModalFooter>
            <button
              type="button"
              onClick={() => { setShowCreate(false); setForm(EMPTY_FORM) }}
              className="px-4 py-2 text-sm border rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating}
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {creating ? 'Creating...' : 'Create Client'}
            </button>
          </ModalFooter>
          </form>
        </Modal>
      )}
    </div>
  )
}
